import React from 'react'
import { ExclamationCircleOutlined, ReloadOutlined, ClearOutlined, ToolOutlined } from '@ant-design/icons'
import { Button, Flex } from 'antd'
import { isAndroid, openConfig } from '../utils/androidUtils'

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null, errors: [] }
    this._onWindowError = this._onWindowError.bind(this)
    this._onUnhandledRejection = this._onUnhandledRejection.bind(this)
  }

  static getDerivedStateFromError(error) {
    // Atualiza o state para mostrar a UI de erro (com o erro imediato)
    return { hasError: true, error }
  }

  componentDidMount() {
    this._patchConsole()
    window.addEventListener('error', this._onWindowError, true)
    window.addEventListener('unhandledrejection', this._onUnhandledRejection)
  }

  componentWillUnmount() {
    this._restoreConsole()
    window.removeEventListener('error', this._onWindowError, true)
    window.removeEventListener('unhandledrejection', this._onUnhandledRejection)
  }

  _onWindowError(e) {
    const isErrorEvent = e instanceof ErrorEvent
    const errObj = isErrorEvent ? e.error : null
    const message = isErrorEvent ? e.message : this._formatResourceError(e)
    const stack = errObj && errObj.stack ? errObj.stack : undefined
    const source = isErrorEvent ? `${e.filename}:${e.lineno}:${e.colno}` : undefined
    this._pushError({
      type: isErrorEvent ? 'WindowError' : 'ResourceError',
      message,
      stack,
      source
    })
  }

  _formatResourceError(e) {
    try {
      const t = e.target || e.srcElement
      const node = t && t.tagName ? t.tagName.toLowerCase() : 'desconhecido'
      const src = (t && (t.src || t.href || t.currentSrc || t.data)) || ''
      return `Falha ao carregar ${node} ${src}`
    } catch (_) {
      return 'Erro de recurso'
    }
  }

  _onUnhandledRejection(e) {
    const reason = e.reason
    const message = reason && reason.message ? reason.message : String(reason)
    const stack = reason && reason.stack ? reason.stack : undefined
    this._pushError({
      type: 'UnhandledRejection',
      message,
      stack
    })
  }

  _patchConsole() {
    if (this._consolePatched) return
    this._consolePatched = true
    this._origConsoleError = console.error
    this._origConsoleWarn = console.warn
    const self = this
    console.error = function (...args) {
      try {
        const msg = args
          .map(a => (a && a.stack ? a.stack : typeof a === 'string' ? a : JSON.stringify(a)))
          .join(' ')
        setTimeout(() => self._pushError({ type: 'ConsoleError', message: msg }), 0)
      } catch (_) { }
      self._origConsoleError.apply(console, args)
    }
    console.warn = function (...args) {
      try {
        const msg = args.map(a => (typeof a === 'string' ? a : JSON.stringify(a))).join(' ')
        setTimeout(() => self._pushError({ type: 'ConsoleWarn', message: msg }), 0)
      } catch (_) { }
      self._origConsoleWarn.apply(console, args)
    }
  }

  _restoreConsole() {
    if (!this._consolePatched) return
    console.error = this._origConsoleError
    console.warn = this._origConsoleWarn
    this._consolePatched = false
  }

  _pushError(entry) {
    const withMeta = {
      ...entry,
      timestamp: new Date().toISOString()
    }
    this.setState(prev => {
      const errors = [...(prev.errors || []), withMeta]
      if (errors.length > 200) errors.shift()
      return { errors }
    })
  }

  componentDidCatch(error, errorInfo) {
    // Você pode registrar o erro em um serviço de relatório de erro
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    this._pushError({
      type: 'ReactError',
      message: error && error.message ? error.message : String(error),
      stack: error && error.stack ? error.stack : undefined,
      source: errorInfo && errorInfo.componentStack ? errorInfo.componentStack : undefined
    })

    this.setState({
      error: error,
      errorInfo: errorInfo
    })
  }

  handleReload = () => {
    window.location.reload()
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  // Lógica para limpar cache (similar ao Login.jsx)
  clearCache = () => {
    try {
      localStorage.clear()
      sessionStorage.clear()
      document.cookie.split(';').forEach(function (c) {
        document.cookie = c
          .replace(/^ +/, '')
          .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/')
      })
      if ('caches' in window) {
        caches.keys().then(function (names) {
          for (let name of names) {
            caches.delete(name)
          }
        })
      }
      alert('Cache limpo com sucesso! A página será recarregada.')
      window.location.reload()
    } catch (e) {
      alert('Falha ao limpar cache: ' + (e && e.message ? e.message : String(e)))
    }
  }

  render() {
    if (this.state.hasError) {
      // UI de erro customizada
      return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8" style={{ position: 'relative' }}>
          {isAndroid() && (
            <Button
              type="text"
              icon={<ToolOutlined />}
              onClick={openConfig}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                zIndex: 1000,
                color: '#666',
                border: 'none',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
              title="Ferramentas"
            />
          )}
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="flex justify-center">
              <ExclamationCircleOutlined style={{ fontSize: '48px', color: '#ef4444' }} />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Oops! Algo deu errado
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Ocorreu um erro inesperado. Tente recarregar a página ou voltar ao início.
            </p>
          </div>

          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              <div className="space-y-4">
                <Button
                  onClick={this.handleReload}
                  type="primary"
                  icon={<ReloadOutlined />}
                  block
                >
                  Recarregar Página
                </Button>

                <Button
                  onClick={this.handleGoHome}
                  block
                >
                  Voltar ao Início
                </Button>
              </div>

              {/* Detalhes imediatos do erro renderizado */}
              {this.state.error && (
                <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="text-xs text-red-800 whitespace-pre-wrap overflow-auto max-h-48">
                    <div>
                      <strong>Mensagem</strong>: {this.state.error?.message || String(this.state.error)}
                    </div>
                    {this.state.errorInfo?.componentStack && (
                      <pre className="mt-2">{this.state.errorInfo.componentStack}</pre>
                    )}
                    {this.state.error?.stack && (
                      <pre className="mt-2">{this.state.error.stack}</pre>
                    )}
                  </div>
                </div>
              )}

              <details className="mt-6" open>
                <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                  Erros capturados ({this.state.errors?.length || 0})
                </summary>
                <div className="mt-2 space-y-2">
                  {(this.state.errors || []).map((err, idx) => (
                    <div key={idx} className="p-3 bg-red-50 border border-red-200 rounded-md">
                      <div className="text-xs text-red-800 whitespace-pre-wrap overflow-auto max-h-40">
                        <strong>{err.type}</strong> — <span>{err.timestamp}</span>
                        <div>{err.message}</div>
                        {err.source && <div className="text-red-700">{err.source}</div>}
                        {err.stack && (
                          <pre className="mt-2">{err.stack}</pre>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </details>

              {/* Clear Cache Button - Discreto no footer */}
              <Flex justify="center" style={{ marginTop: '8px' }}>
                <Button
                  type="text"
                  icon={<ClearOutlined />}
                  onClick={this.clearCache}
                  size="small"
                  style={{
                    color: 'rgba(0, 0, 0, 0.45)',
                    border: 'none',
                    fontSize: '12px',
                    height: 'auto',
                    padding: '4px 8px'
                  }}
                  title="Limpar Cache"
                >
                  Limpar cache
                </Button>
              </Flex>
            </div>
          </div>
        </div>
      )
    }

    // Overlay de erros quando a aplicação continua funcionando
    const errors = this.state.errors || []
    const showErrorOverlay = this.props.showErrorOverlay !== false // Por padrão é true, mas pode ser desabilitado

    return (
      <>
        {this.props.children}
        {showErrorOverlay && errors.length > 0 && (
          <div className="fixed bottom-4 right-4 w-96 max-w-full bg-white shadow-lg border border-red-200 rounded-md overflow-hidden z-50">
            <div className="px-3 py-2 bg-red-600 text-white text-sm">
              Erros capturados ({errors.length})
            </div>
            <div className="max-h-64 overflow-auto p-3 space-y-2">
              {errors.slice(-10).map((err, idx) => (
                <div key={idx} className="p-2 bg-red-50 border border-red-200 rounded">
                  <div className="text-xs text-red-800 whitespace-pre-wrap">
                    <strong>{err.type}</strong> — <span>{err.timestamp}</span>
                    <div>{err.message}</div>
                    {err.source && <div className="text-red-700">{err.source}</div>}
                    {err.stack && (
                      <pre className="mt-1">{err.stack}</pre>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    )
  }
}

export default ErrorBoundary